import React from 'react';
import { ArrowRight } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * La carte de section de la page d'accueil: un titre porté par une icône, un
 * lien de sortie à droite, le contenu dessous.
 *
 * Elle existe pour une raison simple: les six blocs de cet écran avaient
 * chacun leur propre façon de poser un titre — `<h2>` nu ici, `CardContent`
 * avec une ligne `flex` là, un `PageHeader` ailleurs. Les hauteurs d'en-tête
 * ne tombaient pas d'accord, et deux cartes côte à côte ne s'alignaient pas.
 */

export const SectionCard = ({
  icon: Icon,
  title,
  subtitle,
  /** Le titre passe à la ligne au lieu d'être coupé par des points de suspension. */
  titreMultiligne = false,
  actionLabel,
  onAction,
  /**
   * Recherche et filtres posés dans l'en-tête, à droite du titre, au lieu
   * d'une rangée de plus sous lui. En petit écran ils passent dessous.
   */
  outils,
  children,
  className,
  contentClassName,
  ...rest
}) => (
  <section
    className={cn(
      'rounded-2xl border border-border/70 bg-card shadow-sm',
      className,
    )}
    {...rest}
  >
    <header
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 px-4 pt-4 md:px-5 md:pt-5',
        // Avec des outils, le titre se tronque en grand écran plutôt que de
        // les renvoyer sur une seconde ligne.
        outils && 'xl:flex-nowrap',
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        {Icon ? (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Icon className="h-[18px] w-[18px]" />
          </span>
        ) : null}
        <div className="min-w-0">
          <h2 className={cn('text-base font-bold leading-tight text-foreground', titreMultiligne ? 'text-balance' : 'truncate')}>
            {title}
          </h2>
          {subtitle ? (
            <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
          ) : null}
        </div>
      </div>

      {outils ? (
        <div className="flex w-full flex-wrap items-center gap-2 xl:w-auto xl:max-w-[72%] xl:flex-none xl:justify-end">
          {outils}
        </div>
      ) : null}

      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          className="group inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-semibold text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {actionLabel}
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
        </button>
      ) : null}
    </header>

    <div className={cn('px-4 pb-4 pt-4 md:px-5 md:pb-5', contentClassName)}>
      {children}
    </div>
  </section>
);

export default SectionCard;
